/**
 * Where the access token lives between one page load and the next, and why it
 * lives there.
 *
 * ## The decision: `sessionStorage`
 *
 * Three places were available and each buys something different against the one
 * attack that matters, which is a script running on this origin.
 *
 * **None of the three survives XSS.** A script on the page reads `localStorage`
 * and `sessionStorage` alike, and it can also read a variable held in a module
 * closure — by calling the same exported function every legitimate caller calls,
 * or simply by waiting for the next request and reading it off the wire. "In
 * memory" is not a defence against script execution on the origin; it is a
 * defence against *persistence*, and that is a smaller and more honest claim.
 * The only storage an injected script genuinely cannot read is an `httpOnly`
 * cookie, and that needs the API to set one — a schema and endpoint change this
 * package cannot make (`TODO.md` #391).
 *
 * So the choice is between the two things storage *does* decide: how long a
 * stolen-device or shared-browser window stays open, and whether a reload costs
 * a password.
 *
 * - `localStorage` keeps the token until something deletes it. On a shared or
 *   public machine, closing the tab leaves a live session behind for eight hours
 *   (`ACCESS_TOKEN_TTL_SECONDS`), and nothing can revoke it — there is no
 *   session table, no refresh token and no deny-list, so **the expiry is the
 *   entire revocation story** (`TODO.md` #391). That makes persistence past the
 *   tab a real cost with no compensating control.
 * - Memory only would mean every reload is a fresh password, which the game
 *   cannot afford: a reload mid-descent is a common thing to do and the brief
 *   requires that it lands back in the world.
 * - `sessionStorage` survives a reload and dies with the tab. It is also scoped
 *   per tab, so two accounts can be signed in side by side without one
 *   overwriting the other.
 *
 * **What it costs, stated plainly:** an XSS on `d2.ronaldswets.nl` steals the
 * token, and the token then works until it expires no matter what the real owner
 * does — changing their password does not stop it. That is a property of the
 * token design and not of the storage, and it is the reason this file cannot be
 * the last word on the subject.
 *
 * ## Everything read out of here is input
 *
 * Storage is writable by anything on this origin, including a person with the
 * developer tools open, so the stored value is parsed exactly as suspiciously as
 * a request body: a shape that is not a session is no session at all. And every
 * call is wrapped, because Web Storage genuinely throws — a quota, a private
 * browsing mode, a blocked third-party context — and a page that cannot store a
 * token should still run for as long as it is loaded.
 */

/** The subset of Web Storage this needs. Narrow, so a test supplies a `Map` and never a browser. */
export interface WebStorageLike {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
  readonly removeItem: (key: string) => void;
}

/**
 * The key the session is filed under.
 *
 * Namespaced because an origin is shared with whatever else is served from it,
 * and deliberately not called `token` or `jwt`: the name is the first thing
 * anything sweeping a browser's storage matches on, and a signpost costs
 * nothing to remove.
 */
export const SESSION_KEY = 'd2.session';

export interface StoredSession {
  readonly token: string;
  /** The character this tab was last playing, or `null` when none has been chosen. */
  readonly characterId: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const readStoredSession = (storage: WebStorageLike): StoredSession | null => {
  let held: string | null;
  try {
    held = storage.getItem(SESSION_KEY);
  } catch {
    return null;
  }

  if (held === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(held);
  } catch {
    return null;
  }

  if (!isRecord(parsed)) return null;

  const token = parsed['token'];
  if (typeof token !== 'string' || token.length === 0) return null;

  const characterId = parsed['characterId'];
  if (characterId === undefined || characterId === null) return { token, characterId: null };
  // An empty id is not one, and would otherwise become a request for
  // `/api/characters/` — a different route entirely.
  if (typeof characterId !== 'string' || characterId.length === 0) return null;

  return { token, characterId };
};

export const writeStoredSession = (storage: WebStorageLike, session: StoredSession): void => {
  try {
    // Written field by field, so nothing a caller happened to be carrying
    // alongside the session can be persisted by accident.
    storage.setItem(SESSION_KEY, JSON.stringify({ token: session.token, characterId: session.characterId }));
  } catch {
    // Nothing to do and nothing to say: the session lasts this page load.
  }
};

export const clearStoredSession = (storage: WebStorageLike): void => {
  try {
    storage.removeItem(SESSION_KEY);
  } catch {
    // Signing out must never fail. The token is dropped from memory regardless.
  }
};
